// ════════════════════════════════════════════════════════════════════════════
// LpaErrorBoundary — error boundary com mensagens PT-BR amigáveis
//
// Diferenças vs Sentry.ErrorBoundary nativo:
//   • Traduz códigos PostgREST/Auth para PT-BR (apiErrors.ts)
//   • Mostra ação sugerida ao usuário (não só stack trace)
//   • Botão "Tentar novamente" + opcional "Reportar problema" (callback)
//   • Reporta a Sentry automaticamente (re-export do mecanismo padrão)
// ════════════════════════════════════════════════════════════════════════════

import React from "react";
import * as Sentry from "@sentry/react";
import LpaButton from "./LpaButton";
import LpaCard from "./LpaCard";
import { friendlyError } from "../../../shared/apiErrors";

interface Props {
  children: React.ReactNode;
  /** Componente a renderizar se quiser fallback custom (override padrão) */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Callback quando user clica "Reportar" */
  onReport?: (error: Error) => void;
  /** Boundary contextual — útil para tracking */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class LpaErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    Sentry.withScope((scope) => {
      if (this.props.boundaryName) scope.setTag("boundary", this.props.boundaryName);
      scope.setExtra("componentStack", info.componentStack);
      Sentry.captureException(error);
    });
  }

  reset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError || !this.state.error) return this.props.children;

    if (this.props.fallback) return this.props.fallback(this.state.error, this.reset);

    const friendly = friendlyError(this.state.error);
    const isDev = typeof process !== "undefined" && process.env?.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        style={{
          minHeight: 400,
          display: "grid",
          placeItems: "center",
          padding: 24,
          background: "var(--lpa-bg)",
        }}
      >
        <LpaCard variant="elevated" padding="lg" style={{ maxWidth: 480, width: "100%" }}>
          <div
            aria-hidden="true"
            style={{
              fontSize: 36,
              textAlign: "center",
              marginBottom: 12,
              color:
                friendly.severity === "danger"
                  ? "var(--lpa-danger)"
                  : friendly.severity === "warning"
                    ? "var(--lpa-warning)"
                    : "var(--lpa-info)",
            }}
          >
            {friendly.severity === "danger" ? "⚠" : friendly.severity === "warning" ? "⚠" : "ℹ"}
          </div>

          <h2
            style={{
              fontSize: "var(--lpa-text-2xl)",
              fontWeight: 600,
              color: "var(--lpa-text)",
              margin: "0 0 10px",
              textAlign: "center",
              fontFamily: "var(--lpa-font-serif)",
            }}
          >
            {friendly.title}
          </h2>

          <p
            style={{
              fontSize: "var(--lpa-text-md)",
              color: "var(--lpa-sub)",
              lineHeight: 1.6,
              textAlign: "center",
              margin: "0 0 16px",
            }}
          >
            {friendly.description}
          </p>

          {friendly.action && (
            <p
              style={{
                fontSize: "var(--lpa-text-sm)",
                color: "var(--lpa-muted)",
                lineHeight: 1.5,
                textAlign: "center",
                margin: "0 0 20px",
                fontFamily: "var(--lpa-font-mono)",
              }}
            >
              {friendly.action}
            </p>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
            <LpaButton variant="primary" onClick={this.reset}>
              Tentar novamente
            </LpaButton>
            {this.props.onReport && (
              <LpaButton
                variant="ghost"
                onClick={() => this.state.error && this.props.onReport!(this.state.error)}
              >
                Reportar problema
              </LpaButton>
            )}
            <LpaButton variant="secondary" onClick={() => window.location.reload()}>
              Recarregar página
            </LpaButton>
          </div>

          {isDev && (
            <details
              style={{
                marginTop: 18,
                fontFamily: "var(--lpa-font-mono)",
                fontSize: "var(--lpa-text-2xs)",
                color: "var(--lpa-muted)",
              }}
            >
              <summary
                style={{ cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em" }}
              >
                Detalhes técnicos (dev)
              </summary>
              <pre
                style={{
                  marginTop: 6,
                  padding: 8,
                  background: "var(--lpa-surface)",
                  borderRadius: "var(--lpa-radius-sm)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  maxHeight: 160,
                  overflow: "auto",
                  fontSize: 10,
                }}
              >
                {this.state.error.message}
                {"\n"}
                {this.state.error.stack?.split("\n").slice(0, 6).join("\n")}
              </pre>
            </details>
          )}
        </LpaCard>
      </div>
    );
  }
}
